import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sun, Moon, LogOut, Mail, Settings, Database, UserCircle2, ArrowRight } from "lucide-react"; 
import UserManagement from "./UserManagement";
import UsageRecords from "./UsageRecords";
import UserInterface from './UserInterface';
import Email from './Email';
import Device from './Device';
import UberAccount from './UberAccount';

function Dashboard() {
  const [darkMode, setDarkMode] = useState(
    JSON.parse(localStorage.getItem("darkMode")) ?? true
  );
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showDevice, setShowDevice] = useState(false);
  const [showUber, setShowUber] = useState(false);


  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.warn("未找到登入令牌，重定向到登入頁面...");
      window.location.href = "/login";
    } else {
      fetch("https://api.mcvanuatu.com/api/user", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP 狀態 ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        setUser(data);
        setRole(data.role);
      })
      .catch((error) => {
        console.error("獲取用戶數據錯誤:", error.message);
      });
    }
  }, []);

  const handleDarkModeToggle = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem("darkMode", JSON.stringify(newDarkMode));
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  const returnToHome = () => {
    setShowEmail(false);
    setShowAdmin(false);
    setShowDevice(false);
	setShowUber(false);
  };
  
	const toggleUber = () => {
	  setShowUber(prev => !prev);
	  setShowEmail(false);
	  setShowAdmin(false);
	  setShowDevice(false);
	};

  const toggleEmail = () => {
    setShowEmail(prev => !prev);
    setShowAdmin(false);
    setShowDevice(false);
  setShowUber(false);
  };

  const toggleAdmin = () => {
    setShowAdmin(prev => !prev);
    setShowEmail(false);
    setShowDevice(false);
  setShowUber(false);
  };

  const toggleDevice = () => {
    setShowDevice(prev => !prev);
    setShowEmail(false);
    setShowAdmin(false);
  setShowUber(false);
  };

const [loadingAttempts, setLoadingAttempts] = useState(0);

useEffect(() => {
  if (!user) {
    // 設置定時器，每秒檢查一次
    const timer = setInterval(() => {
      setLoadingAttempts(prev => prev + 1);
    }, 1000);

    // 清理函數
    return () => clearInterval(timer);
  }
}, [user]);

useEffect(() => {
  if (loadingAttempts >= 3) {
    console.warn("三次嘗試後仍未加載到用戶資料，重定向到登入頁面...");
    window.location.href = "/login";
  }
}, [loadingAttempts]);

if (!user) {
  return (
    <div className={`flex items-center justify-center min-h-screen ${darkMode ? 
      "bg-[#1E1E1E] text-[#FFFFFF]" : "bg-[#FFFFFF] text-[#121212]"}`}>
      <p className="text-lg font-semibold">載入中...（嘗試次數：{loadingAttempts}/3）</p>
    </div>
  );
}



  return (
    <div className={`min-h-screen ${darkMode ? "bg-[#121212] text-[#FFFFFF]" : "bg-[#FFFFFF] text-[#121212]"}`}>
      <header className={`flex items-center justify-between p-4 ${darkMode ? "bg-[#1E1E1E]" : "bg-[#F5F5F5]"}`}>
        <button 
          onClick={returnToHome}
          className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
        >
          <UserCircle2 className="w-6 h-6" />
          <h1 className="!text-xl font-bold">{user.name || "使用者"}</h1>
        </button>

        <div className="flex items-center space-x-4">
          <button 
            onClick={handleDarkModeToggle} 
            className="p-2 rounded-full bg-transparent hover:bg-opacity-25 transition"
          >
            {darkMode ? (
              <Sun className="w-6 h-6 text-[#FFFFFF] hover:text-[#E0E0E0]" />
            ) : (
              <Moon className="w-6 h-6 text-[#333333] hover:text-[#121212]" />
            )}
          </button>

          <button 
            onClick={handleLogout}
            className="p-2 rounded-full bg-transparent hover:bg-opacity-25 transition"
            title="登出"
          >
            <LogOut 
              className={`w-6 h-6 ${darkMode 
                ? "text-[#FFFFFF] hover:text-[#E0E0E0]" 
                : "text-[#333333] hover:text-[#121212]"}`}
            />
          </button>
        </div>
      </header>

      <main className="p-5 sm:p-8">
        {role === "ADMIN" ? (
          showEmail ? (
            <Email darkMode={darkMode} />
          ) : showAdmin ? (
            <motion.div 
              className={`p-6 rounded-lg shadow-md ${darkMode ? "bg-[#1E1E1E]" : "#FFFFFF"}`}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <UserManagement darkMode={darkMode} />
              <UsageRecords darkMode={darkMode} />
            </motion.div>
          ) : showDevice ? (
            <Device darkMode={darkMode} />
          ) : showUber ? (
      <UberAccount darkMode={darkMode} /> 
    ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.button 
                onClick={toggleAdmin}
                className={`p-6 rounded-xl shadow-lg ${
                  darkMode 
                    ? "bg-[#1E1E1E] hover:bg-[#333333]" 
                    : "#FFFFFF hover:bg-[#F5F5F5]"
                } transition-all duration-100 transform hover:scale-105`}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <Database className="w-12 h-12" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">管理面板</h3>
                    <p className="text-sm opacity-80">查看用戶管理和使用記錄</p>
                  </div>
                  <ArrowRight className="w-6 h-6 opacity-60" />
                </div>
              </motion.button>
              
              <motion.button 
                onClick={toggleEmail}
                className={`p-6 rounded-xl shadow-lg ${
                  darkMode 
                    ? "bg-[#1E1E1E] hover:bg-[#333333]" 
                    : "#FFFFFF hover:bg-[#F5F5F5]"
                } transition-all duration-100 transform hover:scale-105`}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <Mail className="w-12 h-12" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">郵件管理</h3>
                    <p className="text-sm opacity-80">查看和管理系統郵件</p>
                  </div>
                  <ArrowRight className="w-6 h-6 opacity-60" />
                </div>
              </motion.button>
              
              <motion.button 
                onClick={toggleDevice}
                className={`p-6 rounded-xl shadow-lg ${
                  darkMode 
                    ? "bg-[#1E1E1E] hover:bg-[#333333]" 
                    : "#FFFFFF hover:bg-[#F5F5F5]"
                } transition-all duration-100 transform hover:scale-105`}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <Settings className="w-12 h-12" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">設備管理</h3>
                    <p className="text-sm opacity-80">查看和管理系統設備</p>
                  </div>
                  <ArrowRight className="w-6 h-6 opacity-60" />
                </div>
              </motion.button>
			  
<motion.button 
  onClick={toggleUber}
  className={`p-6 rounded-xl shadow-lg ${
    darkMode 
      ? "bg-[#1E1E1E] hover:bg-[#333333]" 
      : "#FFFFFF hover:bg-[#F5F5F5]"
  } transition-all duration-100 transform hover:scale-105`}
  initial={{ opacity: 0, y: 50 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8, delay: 0.3 }}
>
  <div className="flex flex-col items-center text-center space-y-4">
    <UserCircle2 className="w-12 h-12" />
    <div>
      <h3 className="text-xl font-bold mb-2">Uber 帳戶</h3>
      <p className="text-sm opacity-80">管理 Call 車帳號</p>
    </div>
    <ArrowRight className="w-6 h-6 opacity-60" />
  </div>
</motion.button>
			  
            </div>
          )
        ) : (
          <motion.div 
            className={`p-6 rounded-lg shadow-md ${darkMode ? "bg-[#1E1E1E]" : "#FFFFFF"}`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className={`text-2xl font-bold mb-6 ${darkMode ? "text-[#FFFFFF]" : "text-[#121212]"}`}>
              帳戶管理 Account Management
            </h2>
            <UserInterface darkMode={darkMode} user={user} />
          </motion.div>
        )}
      </main>

      <footer className={`text-center py-4 ${darkMode ? "text-[#E0E0E0]" : "text-[#333333]"}`}>
        <p>© 2025 XBER All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Dashboard;
